// tslint:disable: no-inferrable-types
import {
  Component,
  OnDestroy,
  OnInit,
  forwardRef,
  Input
} from '@angular/core';

import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  Validators,
  AbstractControl,
  ValidationErrors,
  FormControl,
} from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { pairwise, startWith, filter, takeUntil, tap } from 'rxjs/operators';
import { BackendObjectRef } from '../lib/backend-integration.service';
import { FormFieldTypeID, FormFieldTypeUserField } from '../lib/form-field-type.model';
import { FormField } from '../lib/form-field.model';

export interface WorkflowFormFieldFormVisibilityValue {
  enabled: boolean;
  operator?: string;
  target?: string;
  value?: string|number|(string|number)[];
  visibility?: string;
}

export interface FormFieldVisibilityOperator {
  id: string, name: string, allowedTargetType: 'primitive'|'discrete';
}

@Component({
    selector: 'eas-workflow-form-field-form-visibility',
    styleUrls: ['./form-field-form-visibility.component.css'],
    templateUrl: './form-field-form-visibility.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WorkflowFormFieldFormVisibilityComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => WorkflowFormFieldFormVisibilityComponent),
            multi: true
        }
    ],
    standalone: false
})
export class WorkflowFormFieldFormVisibilityComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {

  @Input() availableFields: FormField[] = [];

  public readonly availableOperators: FormFieldVisibilityOperator[] = [
    { id: 'eq',      name: $localize `equals` , allowedTargetType: 'primitive'},
    { id: 'has_any', name: $localize `has any`, allowedTargetType: 'discrete'},
    { id: 'has_all', name: $localize `has all`, allowedTargetType: 'discrete'},
  ];

  public readonly availableVisibility: BackendObjectRef[] = [
    { id: 'visible', name: $localize `Visible`},
    { id: 'hidden', name: $localize `Hidden`}
  ];

  private isEnabled$$ = new BehaviorSubject<boolean>(false);
  private isEnabled$ = this.isEnabled$$.asObservable();
  private isNotEnabled$ = this.isEnabled$.pipe(filter(value => !value));
  public get isEnabled(): boolean { return this.isEnabled$$.value; }

  public readonly form: FormGroup;

  public targetIsBooleanType: boolean = false;
  public targetHasDiscreteValues: boolean = false;
  public targetDiscreteValues: BackendObjectRef[] = [];
  public targetScopedOperators: FormFieldVisibilityOperator[] = [];

  private destroyed$ = new Subject<void>();

  private getTargetDiscreteValues(targetID: string): BackendObjectRef[] {
    const target = this.availableFields.find(t => t.id === targetID);
    if (!target) { return []; }

    const values = target.type instanceof FormFieldTypeUserField
                   ? target.type.availableValues
                   : target.availableValues;
    return values ?? [];
  }

  private getTargetScopedOperators(targetID: string): FormFieldVisibilityOperator[] {
    return this.availableOperators.filter(o => {
      const isDiscrete = this.isTargetDiscreteType(targetID);
      return isDiscrete
             ? o.allowedTargetType === 'discrete'
             : o.allowedTargetType === 'primitive';
    });
  }

  public isTargetBooleanType(targetID: string): boolean {
    const field = this.availableFields.find(t => t.id === targetID);
    if (!field) { return false; }
    return field.type.id === FormFieldTypeID.boolean || field.type.type === FormFieldTypeID.boolean;
  }

  public isTargetDiscreteType(targetID: string): boolean {
    const field = this.availableFields.find(t => t.id === targetID);
    if (!field) { return false; }
    return field.type.isDiscrete;
  }

  constructor(private formBuilder: FormBuilder) {
    const controls: {[K in keyof WorkflowFormFieldFormVisibilityValue]: any} = {
      enabled: [false]
    };
    this.form = formBuilder.group(controls);
  }

  //
  // Lifecycle
  //

  ngOnInit(): void {
    // When user enable/disable visibility we need to adjust the form
    // accordingly.
    this.getEnabledControl().valueChanges.subscribe((enabled: boolean) => {
      // When not enabled we remove controls for validation
      if (!enabled) {
        this.removeVisibilityControls();
        this.isEnabled$$.next(false);
        return;
      }

      // When enabled we add controls for validation
      this.addVisibilityControls();
      this.isEnabled$$.next(true);

      // When target changes, it may change to Discrete type and we need to
      // get ready to change for the Select input.
      this.getTargetControl()
          .valueChanges
          .pipe(
            startWith(this.getTargetControl().value),
            pairwise(),
            takeUntil(this.isNotEnabled$)
          )
          .subscribe(([oldTarget, newTarget]) => {
            this.targetIsBooleanType = this.isTargetBooleanType(newTarget);
            this.targetHasDiscreteValues = this.isTargetDiscreteType(newTarget);
            this.targetDiscreteValues = this.getTargetDiscreteValues(newTarget);
            this.targetScopedOperators = this.getTargetScopedOperators(newTarget);

            //this.updateControlForTarget(newTarget);
            if (oldTarget !== newTarget && (oldTarget ?? '') !== '') {
              this.form.patchValue({value: undefined});
            }
          });
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private removeVisibilityControls() {
    ['operator', 'target', 'value', 'visibility'].forEach(key => {
      this.form.removeControl(key);
    });
  }

  private addVisibilityControls() {
    this.form.addControl('target', this.formBuilder.control('', Validators.required));
    this.form.addControl('operator', this.formBuilder.control('eq', Validators.required));
    this.form.addControl('value', this.formBuilder.control(undefined));
    this.form.addControl('visibility', this.formBuilder.control('visible', Validators.required));
  }

  private getTargetControl(): FormControl {
    return this.form.get('target') as FormControl;
  }

  private getEnabledControl(): FormControl {
    return this.form.get('enabled') as FormControl;
  }

  //
  // Validator
  //

  validate(_control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {
      invalidConditionalVisibility: {
        valid: false,
        message: $localize `Form field conditional visibility settings is invalid!`
      }
    };
    // tslint:disable-next-line: no-null-keyword
    return this.form.valid ? null : errors;
  }

  //
  // ControlValueAccessor
  //

  public onTouched: () => void = () => { };

  writeValue(value?: WorkflowFormFieldFormVisibilityValue): void {
    if (!value) { return; }

    const defaults: WorkflowFormFieldFormVisibilityValue = { enabled: false };

    if (value.target &&
        this.isTargetDiscreteType(value.target) &&
        value.value !== undefined
       ) {
        value.value = value.value instanceof Array ? value.value : [value.value];
       }

    this.form.setValue(value.enabled ? {...defaults, ...value} : defaults);
  }

  registerOnChange(fn: any): void {
    this.form.valueChanges
             .pipe(
               tap(() => this.onTouched()),
               takeUntil(this.destroyed$)
             )
             .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.form.enabled && isDisabled) {
      this.form.disable();
    }
    if (this.form.disabled && !isDisabled) {
      this.form.enable();
    }
  }
}
